import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Lock, ArrowLeft } from '@phosphor-icons/react';

export default function Privacy() {
  return (
    <div className="min-h-screen py-12">
      <SEO
        title="Privacy Policy — How We Protect Your Data"
        description="Grade9Ready's privacy policy. Learn how we collect, use, and protect personal data for students and parents under UK GDPR and the Data Protection Act 2018. Children's data handled with extra care."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-primary rounded-xl border-2 border-foreground flex items-center justify-center shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <Lock size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold">Privacy Policy</h1>
            <p className="text-muted-foreground text-sm">Grade9Ready Privacy Policy</p>
          </div>
        </div>

        <div className="prose prose-sm max-w-none space-y-8" data-testid="privacy-content">
          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Grade9Ready ("we", "us", "our") is committed to protecting your personal data and respecting your privacy.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              This Privacy Policy explains how we collect, use, store, and protect personal data when you use our platform, in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">2. Who We Are (Data Controller)</h2>
            <p className="text-muted-foreground mb-2">Grade9Ready is the data controller responsible for your personal data.</p>
            <p className="text-muted-foreground"><strong>Email:</strong> <a href="mailto:datacontroller@grade9ready.com" className="text-primary hover:underline">datacontroller@grade9ready.com</a></p>
            <p className="text-muted-foreground"><strong>Business Name:</strong> Grade9Ready</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">3. The Data We Collect</h2>
            <p className="text-muted-foreground mb-3">We may collect and process the following types of personal data:</p>

            <h3 className="text-base font-bold mt-4 mb-2">3.1 Student Data</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Name (or username)</li>
              <li>Email address</li>
              <li>Progress data (e.g. performance, results, activity)</li>
              <li>Account activity and usage data</li>
            </ul>

            <h3 className="text-base font-bold mt-4 mb-2">3.2 Parent/Guardian Data</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Name</li>
              <li>Email address</li>
              <li>Linked student account information (via pairing system)</li>
            </ul>

            <h3 className="text-base font-bold mt-4 mb-2">3.3 Technical Data</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>IP address</li>
              <li>Device type and browser</li>
              <li>Log data (login times, interactions)</li>
            </ul>

            <h3 className="text-base font-bold mt-4 mb-2">3.4 Payment Data</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Billing information (processed securely via third-party providers)</li>
              <li>We do not store full payment card details</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">4. How We Collect Data</h2>
            <p className="text-muted-foreground mb-2">We collect data when you:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Register for an account</li>
              <li>Use the platform</li>
              <li>Link a parent/guardian account (via pairing code system)</li>
              <li>Contact us</li>
              <li>Make a purchase</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">5. How We Use Your Data (Legal Basis)</h2>
            <p className="text-muted-foreground mb-3">Under UK GDPR, we rely on the following lawful bases:</p>

            <h3 className="text-base font-bold mt-3 mb-1">5.1 Contract</h3>
            <p className="text-muted-foreground mb-1">To provide our services, including:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Creating and managing accounts</li>
              <li>Delivering learning content</li>
              <li>Tracking progress</li>
            </ul>

            <h3 className="text-base font-bold mt-3 mb-1">5.2 Legitimate Interests</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Improving platform performance</li>
              <li>Monitoring usage for security</li>
              <li>Preventing fraud or misuse</li>
            </ul>

            <h3 className="text-base font-bold mt-3 mb-1">5.3 Legal Obligation</h3>
            <p className="text-muted-foreground mb-3">Compliance with applicable laws and regulations</p>

            <h3 className="text-base font-bold mt-3 mb-1">5.4 Consent (where applicable)</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Marketing communications (if opted in)</li>
              <li>Certain analytics or cookies (where required)</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">6. Children's Data</h2>
            <p className="text-muted-foreground mb-3">Grade9Ready is designed for use by children under 18. We take additional steps to protect children's data:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Data collection is limited to what is necessary</li>
              <li>No public sharing of student data</li>
              <li>Parent/guardian access is controlled via a secure pairing code system</li>
              <li>We do not knowingly collect unnecessary sensitive data</li>
            </ul>
            <p className="text-muted-foreground mt-3">Parents/guardians may request access to or deletion of their child's data.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">7. Parent-Student Linking (Pairing Code System)</h2>
            <p className="text-muted-foreground mb-2">To protect student accounts, a parent/guardian must enter both:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>The student's email address</li>
              <li>A valid, time-limited pairing code</li>
            </ul>
            <p className="text-muted-foreground mt-2">This ensures that only authorised individuals can access student-linked data.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">8. Data Sharing</h2>
            <p className="text-muted-foreground mb-3 font-bold">We do not sell personal data.</p>
            <p className="text-muted-foreground mb-2">We may share data with:</p>

            <h3 className="text-base font-bold mt-3 mb-1">8.1 Service Providers</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-2">
              <li>Hosting providers</li>
              <li>Payment processors</li>
              <li>Analytics tools</li>
            </ul>
            <p className="text-muted-foreground mb-3">All third parties are required to process data securely and comply with data protection laws.</p>

            <h3 className="text-base font-bold mt-3 mb-1">8.2 Legal Requirements</h3>
            <p className="text-muted-foreground">We may disclose data if required by law or to protect rights, safety, or security.</p>

            <h3 className="text-base font-bold mt-3 mb-1">8.3 Voluntary Achievement Sharing</h3>
            <p className="text-muted-foreground">Students may choose to generate and share achievement cards externally (e.g., via social media or messaging apps). These cards contain only the student's first name and general progress statistics (XP, level, streak, topics completed, expected grade). No email addresses, account details, or sensitive personal data are included. Sharing is entirely voluntary and initiated by the student. Grade9Ready does not publish or host shared content publicly on the platform.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">9. International Data Transfers</h2>
            <p className="text-muted-foreground mb-2">If data is transferred outside the UK, we ensure appropriate safeguards are in place, such as:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>UK adequacy regulations</li>
              <li>Standard contractual clauses</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">10. Data Retention</h2>
            <p className="text-muted-foreground mb-2">We retain personal data only as long as necessary:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li><strong>Active accounts:</strong> retained while in use</li>
              <li><strong>Inactive accounts:</strong> deleted or anonymised after a reasonable period</li>
              <li>Legal obligations may require longer retention</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">11. Your Rights (UK GDPR)</h2>
            <p className="text-muted-foreground mb-2">You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Access your data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion ("right to be forgotten")</li>
              <li>Restrict processing</li>
              <li>Object to processing</li>
              <li>Data portability</li>
            </ul>
            <p className="text-muted-foreground mt-3">To exercise your rights, contact: <a href="mailto:datacontroller@grade9ready.com" className="text-primary hover:underline">datacontroller@grade9ready.com</a></p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">12. Data Security</h2>
            <p className="text-muted-foreground mb-2">We implement appropriate technical and organisational measures, including:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Secure servers and encryption where appropriate</li>
              <li>Access controls</li>
              <li>Monitoring for suspicious activity</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">13. Cookies and Tracking</h2>
            <p className="text-muted-foreground mb-2">We may use cookies and similar technologies to:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Improve user experience</li>
              <li>Analyse usage</li>
              <li>Support functionality</li>
            </ul>
            <p className="text-muted-foreground mt-2">Where required, we will request your consent.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">14. Complaints</h2>
            <p className="text-muted-foreground mb-3">If you are unhappy with how we handle your data, you can contact us directly.</p>
            <p className="text-muted-foreground mb-2">You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):</p>
            <p className="text-muted-foreground"><strong>Website:</strong> <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.ico.org.uk</a></p>
            <p className="text-muted-foreground"><strong>Helpline:</strong> 0303 123 1113</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">15. Changes to This Policy</h2>
            <p className="text-muted-foreground">We may update this Privacy Policy from time to time. We will notify users of significant changes where appropriate.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
